import { Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { FixturePricingState } from '@cds/betting-offer/add-ons';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { MeetingModel, WinGameFixturePreview } from '@frontend/sports/racing/feature/models';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';

import { Promotion } from '../../../crm-offer-data/crm-offer.model';
import { CrmPromotionProviderService, PromotionMap } from '../../../crm-promotion/crm-promotion-provider.service';
import { CrmPromotionMapperService } from '../../services/crm-promotion-mapper.service';

interface HorsesMeetingAllRaceViewModel {
    fixture: WinGameFixturePreview;
    pricingState: FixturePricingState;
    fixtureUrl: string;
    startDate: string;
    raceName: string;
}

interface HorsesMeetingAllComponentViewModel {
    races: HorsesMeetingAllRaceViewModel[];
}

@HooksWireup()
@Component({
    selector: 'ms-horses-meeting-all',
    templateUrl: 'horses-meeting-all.component.html',
    standalone: false,
})
export class HorsesMeetingAllComponent extends OnRouteResolve<MeetingModel | undefined> {
    private model?: MeetingModel;
    crmBannerPromotions: Promotion[] = [];
    promotedFixtureIdsMap: StringDictionary<string> = {};

    vm: HorsesMeetingAllComponentViewModel = {
        races: [],
    };

    pricingStates = {
        Resulted: FixturePricingState.Resulted,
        Priced: FixturePricingState.Priced,
        RaceOff: FixturePricingState.RaceOff,
        None: FixturePricingState.None,
    };

    constructor(
        public sitecore: Sitecore,
        private trackingService: TrackingService,
        private urlHelper: UrlHelperService,
        private crmPromotionProvider: CrmPromotionProviderService,
        private promotionMapper: CrmPromotionMapperService,
        route: ActivatedRoute,
    ) {
        super(route);
    }

    onRouteResolved(model?: MeetingModel): void {
        this.model = model;

        this.vm = this.buildViewModel();

        this.crmPromotionProvider.promotions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((promos) => this.promotionsChanged(promos));
        this.crmPromotionProvider.promotionsMap$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ids) => this.promoFixtureIdsChanged(ids));
    }

    onFixtureClick(fixture: WinGameFixturePreview): void {
        this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, {
            [trackingConstants.PAGE_NAME]: `Meeting/All/${fixture.id}`,
        });
    }

    private promoFixtureIdsChanged = (promoMap: PromotionMap) => {
        this.promotedFixtureIdsMap = promoMap.fixtures;
    };

    private promotionsChanged = (promotions: Promotion[]) => {
        if (this.model?.fixtures.length) {
            this.crmBannerPromotions = this.promotionMapper.getMeetingPromotions(this.model.fixtures[0], promotions);
        }
    };

    private raceName(fixture: WinGameFixturePreview): string {
        const handicap = fixture.addons.handicap ? ` ${this.sitecore.horseRacing.common.Handicap}` : '';

        return `${fixture.addons.distance || ''}${handicap} ${
            fixture.addons.courseType !== 'Undefined' && fixture.addons.courseType ? fixture.addons.courseType : ''
        }`;
    }

    private getFixtureUrl(fixture: WinGameFixturePreview): string {
        return this.urlHelper.getRacingUrlByFixture(fixture.sport.id, fixture);
    }

    private buildViewModel(): HorsesMeetingAllComponentViewModel {
        if (!this.model) {
            return { races: [] };
        }

        return {
            races: this.model.fixtures.map((f) => ({
                fixture: f,
                pricingState: f.addons.pricingState || FixturePricingState.None,
                fixtureUrl: this.getFixtureUrl(f),
                startDate: f.startDate,
                raceName: this.raceName(f),
            })),
        };
    }
}
